import React, { useEffect } from "react";
import { clearAuthState } from "@noom/hive-auth";
import { useLogger } from "@noom/hive-analytics";

import { LOGIN_ERROR_MESSAGE_KEY } from "@modules/common/constants";
import { trackRumLogout } from "@app/rumMonitoring";
import { BrandNeutralLoading } from "common/NoomLoader";

import { ProfileLoadError } from "./ProfileLoadError";
import { clearLocalSessionState } from "./session";
import {
  NOT_ENROLLED_MESSAGE,
  usePatientProfile,
} from "./PatientProfileProvider";

/**
 * Route guard that enforces the telehealth-patient (Layer 2) gate.
 *
 * This is the relocated enforcement that used to live inside
 * `PatientProfileProvider`. The provider now only fetches the profile and
 * reports a `status`; this guard turns a `not-patient` result into the
 * pre-migration behavior — surface the enrollment error and tear down the
 * session so `SecureRoute` redirects to `/login`.
 *
 * Routes opt out via `requiresPatient: false` (see `getRoutes()`), which lets
 * pages like `/care` stay open to non-telehealth users.
 */
export const RequirePatient = ({ children }: { children: React.ReactNode }) => {
  const { status } = usePatientProfile();
  const mixpanelLogger = useLogger();

  useEffect(() => {
    if (status === "not-patient") {
      // Report before the teardown so the login screen this produces is
      // attributed to us rather than counted as a lost session.
      trackRumLogout("not_enrolled");
      // Same teardown as logout: clears cookies (incl. the native auto-login
      // cookie, so LoginPage's `useAutoCookieFlow` can't silently re-auth the
      // WebView user and loop), the cached profile, and Mixpanel identity.
      clearLocalSessionState(mixpanelLogger);
      clearAuthState();
      // Set AFTER the teardown above (which clears sessionStorage) so the
      // relayed enrollment error survives for LoginPage to display.
      sessionStorage.setItem(LOGIN_ERROR_MESSAGE_KEY, NOT_ENROLLED_MESSAGE);
    }
  }, [status, mixpanelLogger]);

  // While the profile resolves — or while the session is being torn down for a
  // non-patient — show a loader rather than flashing the gated page.
  if (status === "loading" || status === "not-patient") {
    return <BrandNeutralLoading />;
  }

  // A transient (non-400/404) fetch failure leaves `profile` null. Gated pages
  // assume a populated profile, so block the render and offer a retry instead
  // of flashing a blank/broken page.
  if (status === "error") {
    return <ProfileLoadError />;
  }

  // `patient` — profile is populated, render the gated page.
  return <>{children}</>;
};
